import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle, ArrowRight, Calendar, CreditCard, Ticket, CheckCircle, Clock, Copy, Edit, ExternalLink, Heart, Loader2, Mail, MessageSquare,
  Phone, Play, RefreshCw, Stethoscope, User, Video,
} from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { avatarUrl } from "../../lib/avatar";
import consultationService from "../../services/consultationService";
import DoctorsShadcn from "../../pages/DoctorsShadcn";
import StatusPill from "../common/StatusPill";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

const STATUS_TONE = { Completed: "green", "In Progress": "blue", Active: "violet", Started: "amber", Upcoming: "blue", Abandoned: "amber", Cancelled: "red" };

const label = { fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", color: "hsl(var(--muted-foreground))" };

const Section = ({ icon: Icon, title, children }) => (
  <div style={{ border: "1px solid hsl(var(--border))", borderRadius: 12, overflow: "hidden" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 14px", background: "hsl(var(--muted) / 0.5)", borderBottom: "1px solid hsl(var(--border))" }}>
      <Icon size={13} color="hsl(var(--primary))" />
      <span style={{ fontSize: 12, fontWeight: 600, color: "hsl(var(--foreground))" }}>{title}</span>
    </div>
    <div style={{ padding: "12px 14px" }}>{children}</div>
  </div>
);

const Field = ({ name, children, mono }) => (
  <div style={{ minWidth: 0 }}>
    <div style={label}>{name}</div>
    <div className={mono ? "num" : undefined} style={{ marginTop: 3, fontSize: 12.5, color: "hsl(var(--foreground))", wordBreak: "break-word" }}>{children || "—"}</div>
  </div>
);

const Person = ({ icon: Icon, role, name, lines, onOpen, disabled, tone, image }) => (
  <div style={{ border: "1px solid hsl(var(--border))", borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 14px 12px" }}>
      <div style={{ width: 42, height: 42, flexShrink: 0, borderRadius: "50%", overflow: "hidden", background: `hsl(var(--tint-${tone}))`, border: `1px solid hsl(var(--tint-${tone}-border))`, color: `hsl(var(--on-${tone}))`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 15 }}>
        {image ? <img src={image} alt="" referrerPolicy="no-referrer" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} onError={(e) => { e.currentTarget.style.display = "none"; }} /> : (name || role)?.charAt(0)?.toUpperCase()}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "hsl(var(--foreground))", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name || "N/A"}</div>
        <div style={{ marginTop: 4 }}><StatusPill tone={tone} icon={Icon}>{role}</StatusPill></div>
      </div>
    </div>
    {lines.filter(Boolean).length > 0 && (
      <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "0 14px 12px" }}>
        {lines.filter(Boolean).map((l, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "hsl(var(--muted-foreground))", minWidth: 0 }}>{l}</div>
        ))}
      </div>
    )}
    {onOpen && <button
      type="button"
      onClick={onOpen}
      disabled={disabled}
      style={{ marginTop: "auto", height: 36, border: "none", borderTop: "1px solid hsl(var(--border))", background: "hsl(var(--muted) / 0.4)", color: "hsl(var(--primary))", fontSize: 12.5, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "'IBM Plex Sans',sans-serif" }}
    >View details <ArrowRight size={13} /></button>}
  </div>
);

const ConsultationDetailsDialog = ({ consultationId, onClose }) => {
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState("");
  const [doctorUid, setDoctorUid] = useState(null);
  const [photos, setPhotos] = useState({ patient: undefined, doctor: undefined });

  const load = useCallback(async () => {
    if (!consultationId) return;
    setLoading(true);
    setError(null);
    try {
      const d = await consultationService.getConsultationById(consultationId);
      setC(consultationService.formatConsultationData(d));
    } catch (e) {
      setError(e.message || "Could not load consultation");
    } finally {
      setLoading(false);
    }
  }, [consultationId]);

  useEffect(() => {
    setC(null);
    setPhotos({ patient: undefined, doctor: undefined });
    load();
  }, [load]);

  // Profile photos live on the Users docs, not on the consultation.
  useEffect(() => {
    if (!c?.participants?.length) return;
    let off = false;
    const grab = async (uid) => {
      try {
        const snap = uid ? await getDoc(doc(db, "Users", uid)) : null;
        return snap?.exists() ? avatarUrl(snap.data()) : undefined;
      } catch (e) { return undefined; }
    };
    Promise.all([grab(c.participants[0]), grab(c.participants[1])]).then(([patient, doctor]) => {
      if (!off) setPhotos({ patient: patient || avatarUrl(c.patientDetails), doctor: doctor || avatarUrl(c.doctorDetails) });
    });
    return () => { off = true; };
  }, [c?.participants]);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1800);
    } catch (e) { /* ignore */ }
  };

  const source = c ? ((c.meetLink ? "web" : c.booking_type) === "web" ? "web" : "app") : "app";
  const room = c?.doctorInRoom && c?.patientInRoom ? "Both in room" : c?.doctorInRoom ? "Doctor in room" : c?.patientInRoom ? "Patient in room" : "Waiting for participants";
  const locked = c?.status === "Completed" || c?.status === "Cancelled";
  const pd = c?.patientDetails;
  const booked = c?.extras?.patientDetails || {};
  const patientEmail = pd?.email || c?.patientEmail || booked.email || "";
  const patientPhone = pd?.phone || c?.patientWhatsApp || booked.phone || "";
  const pay = c?.payment || {};
  const paid = Number(pay.amountPaise || 0) / 100;
  const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);
  const bookedAt = Number(c?.bookedAt || c?.createdAt || 0);
  const dd = c?.doctorDetails;

  return (
    <>
    {doctorUid && <DoctorsShadcn popupOnly openUid={doctorUid} onClosePopup={() => setDoctorUid(null)} />}
    <Dialog open={!!consultationId && !doctorUid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={{ maxWidth: 680, maxHeight: "90vh", overflowY: "auto" }}>
        <DialogHeader>
          <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            Consultation
            {c && <StatusPill mono>#{c.consultationId?.slice(-8) || "N/A"}</StatusPill>}
            {c && <StatusPill tone={STATUS_TONE[c.status] || "neutral"}>{c.status}</StatusPill>}
          </DialogTitle>
          <DialogDescription>{c ? `${c.formattedConsultationTime} · ${c.duration}` : "Loading details…"}</DialogDescription>
        </DialogHeader>

        {loading && !c && (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Loader2 size={22} className="animate-spin" color="hsl(var(--primary))" /></div>
        )}

        {error && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 14, borderRadius: 10, background: "hsl(var(--tint-red))", border: "1px solid hsl(var(--tint-red-border))", color: "hsl(var(--on-red))", fontSize: 12.5 }}>
            <AlertCircle size={16} /> <span style={{ flex: 1 }}>{error}</span>
            <button type="button" onClick={load} style={{ display: "flex", alignItems: "center", gap: 4, border: "none", background: "transparent", color: "inherit", fontWeight: 600, cursor: "pointer" }}><RefreshCw size={12} />Retry</button>
          </div>
        )}

        {c && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
              <Person
                icon={User} tone="blue" role="Patient" name={c.patientName} image={photos.patient}
                lines={[
                  patientEmail && <><Mail size={12} /><span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{patientEmail}</span></>,
                  patientPhone && <><Phone size={12} /><span className="num">{patientPhone}</span></>,
                ]}
              />
              <Person
                icon={Stethoscope} tone="green" role="Doctor" name={c.doctorName} image={photos.doctor}
                disabled={!(c.participants?.length > 1)}
                onOpen={() => setDoctorUid(c.participants[1])}
                lines={[
                  dd?.specialty && <>{dd.specialty}</>,
                  dd?.experience && <><Clock size={12} /><span className="num">{dd.experience}</span>&nbsp;years experience</>,
                ]}
              />
            </div>

            <Section icon={Calendar} title="Schedule">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                <Field name="Scheduled" mono>{c.formattedConsultationTime}</Field>
                <Field name="Duration" mono>{c.duration}</Field>
                {c.actualStartTime && <Field name="Started" mono>{c.formattedActualStartTime}</Field>}
                {c.actualEndTime && <Field name="Ended" mono>{c.formattedActualEndTime}</Field>}
              </div>
            </Section>

            {pd?.notesForDoctor && (
              <Section icon={MessageSquare} title="Patient notes">
                <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "hsl(var(--foreground))" }}>{pd.notesForDoctor}</p>
              </Section>
            )}

            <Section icon={Play} title="Consultation">
              {/* ID */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 8px 7px 12px", borderRadius: 9, background: "hsl(var(--muted) / 0.5)", border: "1px solid hsl(var(--border))" }}>
                <span style={{ ...label, flexShrink: 0 }}>ID</span>
                <span className="num" style={{ flex: 1, minWidth: 0, fontSize: 12, color: "hsl(var(--foreground))", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.consultationId}</span>
                {c.consultationId && (
                  <button type="button" onClick={() => copy(c.consultationId, "id")} style={{ flexShrink: 0, height: 24, padding: "0 8px", borderRadius: 6, border: "1px solid hsl(var(--input))", background: "hsl(var(--card))", cursor: "pointer", fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 4, color: copied === "id" ? "hsl(var(--on-green))" : "hsl(var(--muted-foreground))", fontFamily: "'IBM Plex Sans',sans-serif" }}>
                    {copied === "id" ? <><CheckCircle size={11} />Copied</> : <><Copy size={11} />Copy</>}
                  </button>
                )}
              </div>

              {/* Meet link */}
              {c.meetLink && (
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10, padding: "8px 8px 8px 12px", borderRadius: 9, background: "hsl(var(--tint-green) / 0.5)", border: "1px solid hsl(var(--tint-green-border) / 0.7)" }}>
                  <Video size={14} color="hsl(var(--on-green))" style={{ flexShrink: 0 }} />
                  <span className="num" style={{ flex: 1, minWidth: 0, fontSize: 12, color: "hsl(var(--on-green))", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.meetLink}</span>
                  <a href={c.meetLink} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0, height: 24, padding: "0 8px", borderRadius: 6, border: "1px solid hsl(var(--tint-green-border))", background: "hsl(var(--card))", fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 4, color: "hsl(var(--on-green))", textDecoration: "none" }}>
                    <ExternalLink size={11} />Open
                  </a>
                  <button type="button" onClick={() => copy(c.meetLink, "meet")} style={{ flexShrink: 0, height: 24, padding: "0 8px", borderRadius: 6, border: "1px solid hsl(var(--tint-green-border))", background: "hsl(var(--card))", cursor: "pointer", fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 4, color: "hsl(var(--on-green))", fontFamily: "'IBM Plex Sans',sans-serif" }}>
                    {copied === "meet" ? <><CheckCircle size={11} />Copied</> : <><Copy size={11} />Copy</>}
                  </button>
                </div>
              )}
            </Section>

            <Section icon={CreditCard} title="Booking & payment">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                <StatusPill tone={pay.status === "free" || paid === 0 ? "blue" : "green"}>{pay.status === "free" || paid === 0 ? "Free booking" : "Paid"}</StatusPill>
                {pay.via && <StatusPill style={{ textTransform: "capitalize" }}>{pay.via}</StatusPill>}
                {c.appliedCoupon && <StatusPill tone="violet" icon={Ticket}>{c.appliedCoupon}</StatusPill>}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
                <Field name="Booked on" mono>{bookedAt ? new Date(bookedAt).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</Field>
                <Field name="Booking source">{source === "web" ? "Web" : "App"}</Field>
                <Field name="Consultation type">{c.videoConsultDone || c.meetLink ? "Video" : "Phone"}</Field>
                <Field name="Booked for">{[booked.patientName, booked.patientAge && `${booked.patientAge} yrs`, booked.gender, booked.relationship].filter(Boolean).join(" · ")}</Field>
                <Field name="Amount paid" mono>{inr(paid)}</Field>
                <Field name="Doctor fee" mono>{c.doctorFee !== undefined ? inr(Number(c.doctorFee)) : ""}</Field>
                <Field name="Platform fee" mono>{c.platformFee !== undefined ? inr(Number(c.platformFee)) : ""}</Field>
                <Field name="Coupon">{c.appliedCoupon ? `${c.appliedCoupon} (−${inr(Number(c.couponDiscount || 0))})` : "None used"}</Field>
                <Field name="Commission" mono>{c.commissionPercent !== undefined ? `${c.commissionPercent}%` : ""}</Field>
                <Field name="Payment ID" mono>{pay.paymentId}</Field>
                <Field name="Order ID" mono>{pay.orderId}</Field>
                <Field name="Cancelled by doctor">{c.cancelledByDoctor ? "Yes" : "No"}</Field>
              </div>
            </Section>

            {(pd?.medicalHistory || pd?.currentMedications || pd?.allergies) && (
              <Section icon={Heart} title="Health information">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                  {pd.medicalHistory && <Field name="Medical history">{pd.medicalHistory}</Field>}
                  {pd.currentMedications && <Field name="Current medications">{pd.currentMedications}</Field>}
                  {pd.allergies && <Field name="Allergies">{pd.allergies}</Field>}
                </div>
              </Section>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                disabled={locked}
                onClick={() => navigate(`/consultations/${consultationId}/edit`)}
                style={{ height: 34, padding: "0 14px", borderRadius: 9, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--card))", color: "hsl(var(--foreground))", fontSize: 12.5, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.5 : 1, fontFamily: "'IBM Plex Sans',sans-serif" }}
              ><Edit size={13} />Edit</button>
              <button
                type="button"
                onClick={onClose}
                style={{ height: 34, padding: "0 16px", borderRadius: 9, border: "none", background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "'IBM Plex Sans',sans-serif" }}
              >Close</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ConsultationDetailsDialog;
